// Facebook Ads integration card on the Settings page. Two states:
//   • Disconnected — single "Connect Facebook Ads" button that fetches the
//     OAuth URL and full-navigates the browser to it. Facebook redirects back
//     to /settings on completion.
//   • Connected — token-expiry badge, account checklist, save selection, and
//     a disconnect button.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { JarvisCard } from '@/components/JarvisCard'
import { AdAccountSelector } from '@/components/AdAccountSelector'
import { TokenExpiryBadge } from '@/components/TokenExpiryBadge'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import type { FacebookAccountsResponse } from '@/types'

interface Props {
  /** Banner text from URL params after Facebook callback redirect, if any. */
  banner?: { kind: 'success' | 'error'; text: string } | null
  onDismissBanner?: () => void
}

export function FacebookIntegrationCard({ banner, onDismissBanner }: Props) {
  const queryClient = useQueryClient()
  const [pendingSelection, setPendingSelection] = useState<string[] | null>(null)

  const { data, isLoading } = useQuery<FacebookAccountsResponse>({
    queryKey: ['facebook-accounts'],
    queryFn: () => unwrap<FacebookAccountsResponse>(apiClient.get('/integrations/facebook/accounts')),
    enabled: HAS_AUTH,
  })

  // Reset draft selection whenever fresh server data lands.
  useEffect(() => {
    if (data) setPendingSelection(null)
  }, [data?.selectedIds?.join(',')])

  const connect = useMutation({
    mutationFn: () => unwrap<{ url: string }>(apiClient.get('/integrations/facebook/auth-url')),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  const saveSelection = useMutation({
    mutationFn: (selectedIds: string[]) =>
      unwrap(apiClient.patch('/integrations/facebook/accounts', { selectedIds })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facebook-accounts'] })
      setPendingSelection(null)
    },
  })

  const disconnect = useMutation({
    mutationFn: () => unwrap(apiClient.post('/integrations/facebook/disconnect')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['facebook-accounts'] }),
  })

  const effectiveSelection = pendingSelection ?? data?.selectedIds ?? []
  const dirty =
    pendingSelection !== null &&
    JSON.stringify([...pendingSelection].sort()) !==
      JSON.stringify([...(data?.selectedIds ?? [])].sort())

  return (
    <JarvisCard>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
          Facebook Ads
        </h2>
        {data?.connected && (
          <TokenExpiryBadge expiresAt={data.tokenExpiresAt} />
        )}
      </div>

      {banner && (
        <div
          className={`mb-4 px-3 py-2 rounded-md border text-sm flex items-start justify-between gap-3 ${
            banner.kind === 'success'
              ? 'text-jarvis-green border-jarvis-green/30 bg-jarvis-green/10'
              : 'text-jarvis-red border-jarvis-red/30 bg-jarvis-red/10'
          }`}
        >
          <span>{banner.text}</span>
          {onDismissBanner && (
            <button
              onClick={onDismissBanner}
              className="text-xs uppercase tracking-wider opacity-70 hover:opacity-100"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-jarvis-muted">Loading…</p>
      ) : !data?.connected ? (
        <Disconnected
          onConnect={() => connect.mutate()}
          connecting={connect.isPending}
          error={connect.error instanceof Error ? connect.error.message : null}
        />
      ) : (
        <Connected
          accounts={data.accounts}
          selectedIds={effectiveSelection}
          onChange={setPendingSelection}
          onSave={() => saveSelection.mutate(effectiveSelection)}
          onDisconnect={() => disconnect.mutate()}
          dirty={dirty}
          saving={saveSelection.isPending}
          disconnecting={disconnect.isPending}
        />
      )}
    </JarvisCard>
  )
}

function Disconnected({
  onConnect,
  connecting,
  error,
}: {
  onConnect: () => void
  connecting: boolean
  error: string | null
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-jarvis-muted">
        Connect your Facebook ad accounts so Jarvis can sync campaigns and score them every four hours. Approvals stay on Facebook — Jarvis never sees your password.
      </p>
      <button
        onClick={onConnect}
        disabled={connecting}
        className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
      >
        {connecting ? 'Redirecting…' : 'Connect Facebook Ads'}
      </button>
      {error && <p className="text-xs text-jarvis-red">{error}</p>}
    </div>
  )
}

function Connected({
  accounts,
  selectedIds,
  onChange,
  onSave,
  onDisconnect,
  dirty,
  saving,
  disconnecting,
}: {
  accounts: FacebookAccountsResponse['accounts']
  selectedIds: string[]
  onChange: (ids: string[]) => void
  onSave: () => void
  onDisconnect: () => void
  dirty: boolean
  saving: boolean
  disconnecting: boolean
}) {
  return (
    <div className="space-y-4">
      <p className="text-xs uppercase tracking-widest text-jarvis-muted">
        Ad accounts · {selectedIds.length} of {accounts.length} selected
      </p>
      <AdAccountSelector
        accounts={accounts}
        selectedIds={selectedIds}
        onChange={onChange}
        disabled={saving || disconnecting}
      />
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onDisconnect}
          disabled={disconnecting || saving}
          className="text-xs uppercase tracking-wider text-jarvis-muted hover:text-jarvis-red transition-colors disabled:opacity-50"
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect'}
        </button>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-30 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? 'Saving…' : 'Save Selection'}
        </button>
      </div>
    </div>
  )
}
